/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace */

import {
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewChild
} from '@angular/core';

import { Router }                from '@angular/router';

import {
  ValidationErrors,
  FormControl,
  FormGroup,
  Validators,
  AsyncValidatorFn,
  AbstractControl
}                                from '@angular/forms';

import {
  Observable,
  of
}                                from 'rxjs';

import {
  first,
  map,
  switchMap
} from "rxjs/operators";

import { IUserRow }              from '@/screens/administration/i.user.row';
import { Logger }                from '@/util/logger';
import { MatSidenav }            from '@angular/material/sidenav';
import { UserService }           from '@/services/user.service';
import {OrganizationModel} from "@/models/organization.model";


@Component({
  standalone: false,
  templateUrl: 'approve.user.component.html'
})

/**
 *
 * @class ApproveUserComponent
 *
 * @brief approve new users page
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      June 2022
 *
 */
export class ApproveUserComponent implements OnInit {

  @ViewChild('adminsidenav')
  public         nav: MatSidenav;

  /** the userid of the user we are editing */

  private _userid: string                   = '';

  /** snapshot of unchanged user */
  public oldUser: IUserRow                  = null;

  /** any changes for the user attributes */
  public newUser: IUserRow                  = null;

  /** validation controller for the form */
  public  form: FormGroup                   = null;

  /** confirmation of saving */
  public saveMessage: string                = '';

  /** the new password */
  public _password: string                  = '';

  /** the set of all possible organizations */
  public organizations: OrganizationModel[] = [];

  /** subset of organizations the approver is allowing for admin by this new user */
  private _adminOrgs: OrganizationModel[]   = [];

  /** standard constructor */
  constructor(
    private router: Router,
    private users:  UserService,
    private cdRef:  ChangeDetectorRef
  ) {

  }

  /** the approver is enabling/disabling the user as an administrator on the given org */
  toggleOrgAdmin(event, key: string) {

    /* ensure org with 'key' is NOT in the admin list */

    let ua: ApproveUserComponent = this;

    Logger.debug(`[ApproveUser] admin toggle on ${key}:`, event.checked);

    ua._adminOrgs = ua._adminOrgs.filter(x => x.key.toUpperCase() != key.toUpperCase());

    if (event.checked) {

      /* ensure org with 'key' is in the admin list */

      ua._adminOrgs.push(ua.organizations.find(x => x.key.toUpperCase() == key.toUpperCase()));
    }

    ua.cdRef.detectChanges();

    Logger.debug('[ApproveUser] new admin orgs: ', ua._adminOrgs);
  }

  /** on page setup */
  ngOnInit() {

    Logger.debug('[ApproveUser] init...');

    let ua: ApproveUserComponent = this;

    ua.form = new FormGroup({

      name: new FormControl('', [
        Validators.required,
      ]),

      firstname: new FormControl('', [
        Validators.required,
      ]),

      lastname: new FormControl('', [
        Validators.required,
      ]),

      email: new FormControl('', [
          Validators.required,
          Validators.pattern("^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$")
        ],
        ua.emailExistsValidator()
      ),

      userid: new FormControl('', [
          Validators.required,
          Validators.pattern("^[a-zA-Z0-9._%+-]+$")
        ],
        ua.useridExistsValidator()
      ),

      password: new FormControl('')


    });

    ua.form.statusChanges.subscribe((status: string) => {

      Logger.debug(`[ApproveUser] user form status: `, status);

    });

    /* what user are we editing? The URL ends with the 'userid' of the user */

    const url: string = this.router.url;

    this._userid = url.split(`/`).pop();

    Logger.debug(`[ApproveUser] looking for user: ${this._userid} ...`);

    ua.users.dump(ua._userid)
      .pipe(first())
      .subscribe({

        next: (data: any): void => {

          let row: IUserRow = null;

          if (data.result) {

            row = data.result;
          }

          ua.oldUser = Object.assign({}, row);
          ua.newUser = row;

          Logger.debug(`[ApproveUser] user: `, ua.newUser);

          /*
           * fill in the organizations we can choose from for immediately assigning
           * to the user, to make them an org admin directly on approval.
           *
           */

          row['all_organizations'].forEach((item) => {

            ua.organizations.push(new OrganizationModel(item));
          });

          /* update the form control */

          ua.patch(ua.newUser);

        },

        error: (error: any): void => {

          Logger.error(`[ApproveUser] could not fetch user details: `, error);

        }

      });

  }


  /** server side validation of email, make sure its not already assigned to another user. */
  private emailExistsValidator(): AsyncValidatorFn {

    let ua: ApproveUserComponent = this;

    return (control: AbstractControl): Observable<ValidationErrors | null> => {

      return of(control.value).pipe(
        switchMap((email) => ua.users.userByEmail(email).pipe(
          map((data: any) => {

            let row: IUserRow = null;

            if (data.result) {

              row = data.result;
            }

            return (row && (row.userid != ua.oldUser.userid)) ? { exists: true } : null;
          })
        ))
      );
    };
  }

  /** server side validation of usuerid, make sure its not already assigned to another user.*/
  private useridExistsValidator(): AsyncValidatorFn {

    let ua: ApproveUserComponent = this;

    return (control: AbstractControl): Observable<ValidationErrors | null> => {

      return of(control.value).pipe(
        switchMap((userid) => ua.users.userByUserid(userid).pipe(
          map((data: any) => {

            let row: IUserRow = null;

            if (data.result) {

              row = data.result;
            }

            return (row && (row.userid != ua.oldUser.userid)) ? {exists: true} : null;
          })
        ))
      );
    };
  }

  /** helper to update the form display */
  private patch(user: IUserRow) {

    let ua: ApproveUserComponent = this;

    ua.form.patchValue({
      name:      user.display_name,
      firstname: user.first_name,
      lastname:  user.last_name,
      email:     user.email,
      userid:    user.userid
    });

    ua.cdRef.detectChanges();

  }

  /** helper to update a field in the newUser row */
  updateNewUser(field: string, value: any) {

    let ua: ApproveUserComponent = this;

    ua.saveMessage = '';

    if (!ua.newUser) {

      return ;
    }

    ua.newUser[field] = value;

    ua.cdRef.detectChanges();
  }

  /** update the name */
  setName(event: any) {

    this.updateNewUser('display_name', event.target.value);
  }

  /** update the first name */
  setFirstName(event: any) {

    this.updateNewUser('first_name', event.target.value);
  }

  /** update the last name */
  setLastName(event: any) {

    this.updateNewUser('last_name', event.target.value);
  }

  /** update the email */
  setEMail(event: any) {

    this.updateNewUser('email', event.target.value);
  }

  /** update the email */
  setUserid(event: any) {

    this.updateNewUser('userid', event.target.value);
  }

  /** update the password reset value */
  setPassword(event: any) {

    let ua: ApproveUserComponent = this;

    ua._password = event.target.value;

    ua.cdRef.detectChanges();
  }

  /** getter for display_name */
  get name() { return this.form.get('name'); }

  /** getter for first_name */
  get firstname() { return this.form.get('firstname'); }

  /** getter for last_name */
  get lastname() { return this.form.get('lastname'); }

  /** getter for email */
  get email() { return this.form.get('email'); }

  /** getter for userid */
  get userid() { return this.form.get('userid'); }

  /** getter for password */
  get password() { return this.form.get('password')}

  /** getter for the profile picture */
  profilePicture() {

    if (this.newUser && this.newUser.options && this.newUser.options.picture) {

      return this.newUser.options.picture;
    }

    return 'assets/images/profile-512.png';
  }

  /** open or close the side nav panel */
  toggleSidebar() {

    this.nav.toggle();
  }

  /** update the database */
  save() {

    Logger.debug(`[ApproveUser] saving: `, this.newUser);

    let ua: ApproveUserComponent = this;
    let edit: any = {};

    ['userid', 'email', 'display_name', 'last_name', 'first_name'].forEach( (field) => {

      if (ua.newUser[field] == ua.oldUser[field]) {

        /* hasn't changed */

        return;
      }

      edit[field] = ua.newUser[field];

    });

    edit['is_active']    = true;
    edit['is_suspended'] = false;
    edit['is_pending']   = false;

    /* set the password; if they are providing an override */

    if (ua._password.length > 0) {

      edit['password']   = ua._password;
    }

    /* do the edit */

    ua.users.update(ua.oldUser.userid, edit)
      .pipe(first())
      .subscribe({

        next: (data: any): void => {

          let row: IUserRow = null;

          if (data.result) {

            row = data.result;
          }

          Logger.debug(`[ApproveUser] saved: `, row);

          ua.oldUser = Object.assign({}, row);
          ua.newUser = row;

          /* update the form control */

          ua.patch(ua.newUser);

          ua.saveMessage = "Approved!";

          ua.cdRef.detectChanges();

          /*
           * if we have any organizations that the approval has assigned for administration,
           * then do that update as well.
           *
           */

          if (ua._adminOrgs.length) {

            /* we also have to make the user an admin of those organizations... */

            ua.users.linkAdminToOrg(ua.oldUser.userid, ua._adminOrgs.map(x => x.key))
              .pipe(first())
              .subscribe({

                next: (data: any): void => {

                  Logger.error(`[ApproveUser] user linked as admin to orgs!`, data);

                  /* after approval, go back to the user list page */

                  ua.router.navigate(['administration/users']);
                },

                error: (error: any): void => {

                  Logger.debug(`[ApproveUser] could not assign orgs to admin: `, error);

                  ua.saveMessage = error.toString();

                }

              });

          } else {

            /* we're done, just go back to the users listing... */

            Logger.debug(`[ApproveUser] Assigning as administrator for organizations: `, ua._adminOrgs);

            /* after approval, go back to the user list page */

            this.router.navigate(['administration/users']);
          }

        },

        error: (error: any): void => {

          Logger.error(`[ApproveUser] could not save: `, error);

          ua.saveMessage = error.toString();

        }

      });

  }


}

